"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";
import { GetPayloadModel, GetResponseModel } from "@/libs/shared/api/api.types";
import {
  CreateLinkPtModel,
  SubmissionModel,
  SurveyAssignmentModel,
  SurveyModel,
} from "@/libs/surveys/redux/surveys.types";

const tagTypes = [
  "Survey" as const,
  "Surveys" as const,
  "Submission" as const,
  "Submissions" as const,
  "Assignments" as const,
];

export const invalidateSurveys = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api?.queryFulfilled.then(() => {
      tags?.forEach((tag) => {
        api?.dispatch(surveysApi.util.invalidateTags([tag as any]));
      });
    });
  };
};

// Define a service using a base URL and expected endpoints
export const surveysApi = createApi({
  reducerPath: "surveysApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getSurvey: builder.query<SurveyModel, Partial<GetPayloadModel>>({
      query: ({ id }) => ({
        url: `/pt/forms/${id}`,
      }),
      providesTags: ["Survey"],
    }),
    getSubmission: builder.query<SubmissionModel, Partial<GetPayloadModel>>({
      query: ({ id }) => ({
        url: `/pt/forms/submissions/${id}`,
      }),
      providesTags: ["Submission"],
      transformResponse: (response: any) => {
        return {
          ...response.data,
        };
      },
    }),
    getSurveys: builder.query<
      GetResponseModel<SurveyModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/pt/forms/list?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      providesTags: ["Surveys"],
    }),
    getSubmissions: builder.query<
      GetResponseModel<SubmissionModel>,
      Partial<GetPayloadModel>
    >({
      query: ({
        id,
        page,
        perPage,
        name,
        orderBy,
        orderByDirection,
        customerId,
      }) => {
        const base = `/pt/forms/${id}/submissions?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`;
        return {
          url: customerId ? `${base}&customer_id=${customerId}` : base,
        };
      },
      providesTags: ["Submissions"],
    }),
    getSubmissionsForCustomer: builder.query<
      GetResponseModel<SubmissionModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ id, page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/pt/forms/submissions/customers/${id}?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      providesTags: ["Submissions"],
    }),
    getEmbed: builder.query<{ embedLink: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/pt/forms/${id}/embed`,
      }),
    }),
    getCustomerAssignments: builder.query<
      GetResponseModel<SurveyAssignmentModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ id, page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/pt/forms/assignments/customers/${id}?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      transformResponse: (response: any) => {
        return {
          ...response,
          data: response.data
            .filter(
              (assignment: SurveyAssignmentModel) => assignment?.form_data,
            )
            .map((assignment: SurveyAssignmentModel) => ({
              id: assignment?.form_data?.id,
              internal_name: assignment?.form_data?.internal_name,
              healthform_id: assignment?.form_id?.toString(),
              pt_id: assignment?.linkable_id,
              created_at: assignment?.created_at,
              updated_at: assignment?.updated_at,
              deleted_at: null,
              submissions_count: assignment?.submissions_count,
            })),
        };
      },
      providesTags: ["Assignments"],
    }),
    getGroupAssignments: builder.query<
      GetResponseModel<SurveyAssignmentModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ id, page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/pt/forms/assignments/groups/${id}?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      transformResponse: (response: any) => {
        return {
          ...response,
          data: response.data
            .filter(
              (assignment: SurveyAssignmentModel) => assignment?.form_data,
            )
            .map((assignment: SurveyAssignmentModel) => ({
              id: assignment?.form_data?.id,
              internal_name: assignment?.form_data?.internal_name,
              healthform_id: assignment?.form_id?.toString(),
              pt_id: assignment?.linkable_id,
              created_at: assignment?.created_at,
              updated_at: assignment?.updated_at,
              deleted_at: null,
              submissions_count: assignment?.submissions_count,
            })),
        };
      },
      providesTags: ["Assignments"],
    }),
    getSurveyAssignments: builder.query<
      SurveyAssignmentModel[],
      Partial<GetPayloadModel>
    >({
      query: ({ id, page, perPage, name, orderBy, orderByDirection }) => ({
        url: `/pt/forms/${id}/assignments?page=${page}&per_page=${perPage}&search=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
    }),
    newSurvey: builder.mutation<
      { form: SurveyModel },
      { body: { internalName: string } }
    >({
      query: ({ body }) => {
        return {
          url: `pt/forms`,
          method: "POST",
          body,
        };
      },
    }),
    assignSurvey: builder.mutation<
      void,
      { id: string; body: { customer_ids: number[]; group_ids: number[] } }
    >({
      query: ({ id, body }) => {
        return {
          url: `pt/forms/${id}/assign`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Surveys"],
    }),
    deleteSurvey: builder.mutation<void, { id: string }>({
      query: ({ id }) => {
        return {
          url: `pt/forms/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Surveys"],
    }),
    deleteSubmission: builder.mutation<void, { id: string }>({
      query: ({ id }) => {
        return {
          url: `pt/forms/submissions/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Submissions"],
    }),
    createLinkPt: builder.query<
      CreateLinkPtModel,
      { surveyId: string; customerId: string }
    >({
      query: ({ surveyId, customerId }) => ({
        url: `/pt/forms/${surveyId}/create_link_pt/customers/${customerId}`,
        method: "GET",
      }),
    }),
    unassignSurvey: builder.mutation<
      void,
      { id: string; body: { customer_ids: number[]; group_ids: number[] } }
    >({
      query: ({ id, body }) => {
        return {
          url: `pt/forms/${id}/assign`,
          method: "DELETE",
          body,
        };
      },
      invalidatesTags: ["Assignments"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSurveyQuery,
  useGetSubmissionQuery,
  useGetSurveysQuery,
  useGetSubmissionsQuery,
  useGetSubmissionsForCustomerQuery,
  useGetEmbedQuery,
  useGetCustomerAssignmentsQuery,
  useGetGroupAssignmentsQuery,
  useGetSurveyAssignmentsQuery,
  useCreateLinkPtQuery,
  useAssignSurveyMutation,
  useNewSurveyMutation,
  useDeleteSurveyMutation,
  useDeleteSubmissionMutation,
  useUnassignSurveyMutation,
} = surveysApi;
